/**
 * Synthesized convolution reverb impulse response.
 *
 * Generates a "warm hall" IR in pure JS — no audio file fetch. This keeps
 * VuTwin's "ships zero audio bytes" property: every reverb tail is built
 * from `Math.random()` noise + an exponential decay envelope at runtime.
 *
 * The buffer is stereo so the convolver gives a natural sense of space.
 * Channel 0 and channel 1 are decorrelated by virtue of using independent
 * random samples — that subtle phase difference is what creates the
 * "wide" feel without any explicit panning.
 */
export function createWarmHallIR(
  ctx: AudioContext,
  durationSec = 2.2,
  decay = 2.5,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * durationSec);
  const ir = ctx.createBuffer(2, length, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const data = ir.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      // Exponentially decaying noise — the canonical algorithmic reverb.
      const envelope = Math.pow(1 - t, decay);
      const noise = (Math.random() * 2 - 1) * envelope;
      // Gentle high-frequency rolloff over time gives the "warm hall"
      // character (no harsh sizzle in the long tail).
      const warmth = 1 - t * 0.35;
      data[i] = noise * warmth * 0.6;
    }
  }
  return ir;
}
